import { getLogger } from "@expert/logging";
import { useEffect, useState } from "react";

import { LogFlow } from "@/sdk/logging/flows";

import type { AgentSdk } from "../agentSdk";

const logger = getLogger({ module: "useExpertUniversitySdk" });

/** @returns the wrapped ExpertUniversitySdk if in training mode, otherwise the original sdk */
export function useExpertUniversitySdk(isTrainingMode: boolean, sdk: AgentSdk | undefined): AgentSdk | undefined {
    const [sdkState, setSdkState] = useState(sdk);

    useEffect(() => {
        if (!isTrainingMode || !sdk) {
            setSdkState(sdk);
            return;
        }

        /**
         * Flag to track whether the effect has been cleaned up.
         *
         * If the component unmounts or the dependencies change before the async operation finishes then we don't want
         * to call `setSdkState()` as it is no longer applicable.
         *
         * @see https://react.dev/reference/react/useEffect#fetching-data-with-effects
         */
        let ignore = false;

        const wrapSdk = async () => {
            try {
                const { default: ExpertUniversitySdk } = await import("@/expert-university/ExpertUniversitySdk");
                if (ignore) return;
                setSdkState(new ExpertUniversitySdk(sdk) as unknown as AgentSdk);
            } catch (error) {
                if (!ignore) {
                    logger.error(
                        { flow: LogFlow.WorkspaceLoading, error },
                        "Failed to load ExpertUniversitySdk — expert may be stuck on the loader",
                    );
                }
            }
        };

        void wrapSdk();

        return () => {
            ignore = true;
        };
    }, [isTrainingMode, sdk]);

    return sdkState;
}
